// SPDX-License-Identifier: AGPL-3.0-or-later

import {recordVoiceRecoveryDiagnostic} from '@app/features/voice/state/VoiceRecoveryDiagnostics';
import {ConnectionState, RoomEvent, Track, type RemoteTrackPublication, type Room} from 'livekit-client';
import type React from 'react';
import {useEffect} from 'react';

const SAMPLE_INTERVAL_MS = 2000;
const RECEIVE_STALL_MS = 8000;
const RECOVERY_COOLDOWN_MS = 1500;
const MAX_RECOVERY_ATTEMPTS = 3;
const RESUBSCRIBE_PAUSE_MS = 500;

interface TrackReceiveState {
	packetsReceived: number;
	lastProgressAt: number;
	lastRecoveryAt: number;
	recoveryAttempts: number;
	pendingRecovery: boolean;
}

function inboundAudioPacketCount(report: RTCStatsReport | undefined, mediaTrackId: string): number | null {
	if (!report) return null;
	const mediaTrackStatIds = new Set<string>();
	for (const value of report.values()) {
		const stat = value as RTCStats & {trackIdentifier?: string};
		if (stat.type === 'track' && stat.trackIdentifier === mediaTrackId) mediaTrackStatIds.add(stat.id);
	}
	let found = false;
	let total = 0;
	for (const value of report.values()) {
		const stat = value as RTCStats & {
			kind?: string;
			mediaType?: string;
			packetsReceived?: number;
			trackIdentifier?: string;
			trackId?: string;
		};
		if (stat.type !== 'inbound-rtp' || (stat.kind ?? stat.mediaType) !== 'audio') continue;
		if (stat.trackIdentifier !== mediaTrackId && (!stat.trackId || !mediaTrackStatIds.has(stat.trackId))) continue;
		if (typeof stat.packetsReceived !== 'number' || !Number.isFinite(stat.packetsReceived)) continue;
		total += stat.packetsReceived;
		found = true;
	}
	return found ? total : null;
}

function activeRemoteAudioPublications(room: Room): Array<RemoteTrackPublication> {
	const activeSpeakers = new Set(
		room.activeSpeakers
			.filter((participant) => participant.identity !== room.localParticipant.identity && participant.audioLevel > 0)
			.map((participant) => participant.identity),
	);
	const publications: Array<RemoteTrackPublication> = [];
	for (const participant of room.remoteParticipants.values()) {
		if (!activeSpeakers.has(participant.identity)) continue;
		for (const publication of participant.audioTrackPublications.values()) {
			if (publication.kind !== Track.Kind.Audio || !publication.isSubscribed || publication.track?.isMuted) continue;
			publications.push(publication);
		}
	}
	return publications;
}

export const VoiceReceiveHealthMonitor: React.FC<{room: Room}> = ({room}) => {
	useEffect(() => {
		const states = new Map<string, TrackReceiveState>();
		const recoveryTimers = new Set<ReturnType<typeof setTimeout>>();
		let disposed = false;
		let sampling = false;
		let transportRecovering = false;
		let lastDiagnosticState: string | null = null;

		const recordState = (
			state: 'healthy' | 'transport-reconnecting' | 'transport-reconnected' | 'receive-stalled' | 'recovered' | 'unrecoverable',
		) => {
			if (lastDiagnosticState === state) return;
			lastDiagnosticState = state;
			recordVoiceRecoveryDiagnostic(state);
		};

		const recoverPublication = (publication: RemoteTrackPublication, state: TrackReceiveState): void => {
			const now = Date.now();
			if (state.recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
				recordState('unrecoverable');
				return;
			}
			if (now - state.lastRecoveryAt < RECOVERY_COOLDOWN_MS) return;
			state.lastRecoveryAt = now;
			state.recoveryAttempts += 1;
			state.pendingRecovery = true;
			recordVoiceRecoveryDiagnostic('receive-stalled', {
				recoveryAttempt: state.recoveryAttempts,
				packetsReceived: state.packetsReceived,
			});
			recordState('receive-stalled');
			try {
				publication.setSubscribed(false);
				const timer = setTimeout(() => {
					recoveryTimers.delete(timer);
					if (disposed || room.state !== ConnectionState.Connected) return;
					try {
						publication.setSubscribed(true);
					} catch {
						recordState('unrecoverable');
					}
				}, RESUBSCRIBE_PAUSE_MS);
				recoveryTimers.add(timer);
			} catch {
				recordState('unrecoverable');
			}
		};

		const sample = async (): Promise<void> => {
			if (disposed || sampling) return;
			if (room.state === ConnectionState.Reconnecting || transportRecovering) {
				recordState('transport-reconnecting');
				return;
			}
			if (room.state !== ConnectionState.Connected) return;
			sampling = true;
			try {
				const now = Date.now();
				const activePublications = activeRemoteAudioPublications(room);
				const activeTrackSids = new Set(activePublications.map((publication) => publication.trackSid));
				for (const trackSid of states.keys()) {
					if (!activeTrackSids.has(trackSid)) states.delete(trackSid);
				}
				let anyReceiveProgress = false;
				let anyReceiveStalled = false;
				for (const publication of activePublications) {
					const track = publication.track;
					if (!track) continue;
					let packetsReceived: number | null = null;
					try {
						packetsReceived = inboundAudioPacketCount(await track.getRTCStatsReport(), track.mediaStreamTrack.id);
					} catch {
						continue;
					}
					if (packetsReceived === null) continue;
					const previous = states.get(publication.trackSid);
					if (!previous || packetsReceived > previous.packetsReceived || packetsReceived < previous.packetsReceived) {
						if (previous?.pendingRecovery && packetsReceived > previous.packetsReceived) {
							recordVoiceRecoveryDiagnostic('recovered', {recoveryAttempt: previous.recoveryAttempts});
							recordState('recovered');
						}
						states.set(publication.trackSid, {
							packetsReceived,
							lastProgressAt: now,
							lastRecoveryAt: previous?.lastRecoveryAt ?? 0,
							recoveryAttempts: previous?.recoveryAttempts ?? 0,
							pendingRecovery: previous?.pendingRecovery === true && packetsReceived <= previous.packetsReceived,
						});
						anyReceiveProgress = true;
						continue;
					}
					previous.packetsReceived = packetsReceived;
					if (now - previous.lastProgressAt >= RECEIVE_STALL_MS) {
						anyReceiveStalled = true;
						recoverPublication(publication, previous);
					} else {
						anyReceiveProgress = true;
					}
				}
				if (!anyReceiveStalled && (activePublications.length === 0 || anyReceiveProgress)) recordState('healthy');
			} finally {
				sampling = false;
			}
		};

		const onReconnecting = () => {
			transportRecovering = true;
			recordState('transport-reconnecting');
		};
		const onReconnected = () => {
			transportRecovering = false;
			for (const state of states.values()) state.lastProgressAt = Date.now();
			recordState('transport-reconnected');
		};
		room.on(RoomEvent.Reconnecting, onReconnecting);
		room.on(RoomEvent.SignalReconnecting, onReconnecting);
		room.on(RoomEvent.Reconnected, onReconnected);
		const timer = setInterval(() => void sample(), SAMPLE_INTERVAL_MS);
		void sample();

		return () => {
			disposed = true;
			clearInterval(timer);
			for (const recoveryTimer of recoveryTimers) clearTimeout(recoveryTimer);
			room.off(RoomEvent.Reconnecting, onReconnecting);
			room.off(RoomEvent.SignalReconnecting, onReconnecting);
			room.off(RoomEvent.Reconnected, onReconnected);
			states.clear();
		};
	}, [room]);
	return null;
};
